import React, { useCallback, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import useDeployWatcher from '../hooks/useDeployWatcher';

/**
 * Renders a persistent "new version available" banner in the bottom-right
 * when a fresh frontend build is detected. Deliberately not a sonner toast
 * because it needs to be:
 *   - persistent (a hurried user shouldn't lose it to a 5-second auto-dismiss)
 *   - dismissible (the user can defer the refresh if they're mid-task)
 *   - unmistakable (fresh chunk failures are the recurring pain point we're
 *     protecting against — the CTA has to be obvious).
 */
const DeployUpdateBanner = () => {
  // `seen` is a counter that increments each time the hook fires an update.
  // We show the banner whenever `seen` differs from the last dismissed count,
  // so a fresh deploy after a "Later" click re-opens the banner rather than
  // being suppressed forever (previous behaviour after Feb 2026 code review).
  const [seen, setSeen] = useState(0);
  const [dismissedAt, setDismissedAt] = useState(0);

  const handleUpdate = useCallback(() => setSeen((n) => n + 1), []);
  useDeployWatcher(handleUpdate);

  const visible = seen > dismissedAt;

  const reload = () => {
    // Clear the chunk-reload guard so the fresh HTML gets one clean shot at
    // its new chunks; if it fails again the guard will re-arm on the next tick.
    try { sessionStorage.removeItem('epf.chunk_reload_attempted'); } catch {}
    window.location.reload();
  };

  const dismiss = () => setDismissedAt(seen);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] max-w-sm bg-ep-primary text-white rounded-xl shadow-2xl border border-white/10 p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 fade-in"
      role="alert"
      data-testid="deploy-update-banner"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
        <RefreshCw className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-snug mb-0.5">A new version is available</p>
        <p className="text-xs text-white/80 leading-snug mb-3">
          Refresh to get the latest updates and avoid any errors from running an outdated version.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={reload}
            className="text-xs font-medium bg-white text-ep-primary px-3 py-1.5 rounded-md hover:bg-white/95"
            data-testid="deploy-update-reload-btn"
          >
            Refresh now
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="text-xs text-white/80 hover:text-white px-3 py-1.5"
            data-testid="deploy-update-dismiss-btn"
          >
            Later
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-white/60 hover:text-white flex-shrink-0"
        data-testid="deploy-update-close-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DeployUpdateBanner;
